import { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { Phone, RefreshCw, Send, Plus, X } from "lucide-react";
import toast from "react-hot-toast";
import AdminHeader from "@/components/common/AdminHeader";
import TelecallerHeader from "@/components/common/TelecallerHeader";
import GenericTable from "@/components/ui/GenericTable";
import { FormField } from "@/components/ui/FormField";
import Dropdown from "@/components/ui/Dropdown";
import { useRoleContext } from "@/hooks/useRoleContext";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getBranchZoneEmployees, submitTeleRecord, getTeleRecords } from "@/services/telecaller";

interface TeleFormData {
  Purpose: string;
  Name: string;
  Phone_No: string;
  First_Name: string;
  Last_Name: string;
  Call_Status: string;
  Coversation_Status: string;
  Remarks: string;
}

const PURPOSE_OPTIONS = [
  { id: 1, value: "Renewal", label: "Renewal" },
  { id: 2, value: "Verification", label: "Verification" },
  { id: 3, value: "Follow-Up", label: "Follow-Up" },
];

export const CALL_STATUS_OPTIONS = [
  { id: 1, value: "Complete", label: "Complete" },
  { id: 2, value: "In-complete", label: "Incomplete" },
  { id: 3, value: "Call Back", label: "Call Back" },
  { id: 4, value: "Busy", label: "Busy" },
  { id: 5, value: "No Answer", label: "No Answer" },
  { id: 6, value: "Switch Off", label: "Switch Off" },
  { id: 7, value: "Not reachable", label: "Not reachable" },
];

const CONVERSATION_STATUS_OPTIONS = [
  { id: 1, value: "0", label: "Not Interested" },
  { id: 2, value: "1", label: "Interested" },
];

const RECORD_COLUMNS = [
  { key: "Name", label: "Name" },
  { key: "Phone_No", label: "Phone" },
  { key: "Purpose", label: "Purpose" },
  {
    key: "Call_Status",
    label: "Call Status",
    render: (val: any) =>
      val === "Complete"
        ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">Completed</span>
        : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">{val || "Pending"}</span>,
  },
  {
    key: "Coversation_Status",
    label: "Conversation",
    render: (val: any) =>
      val === false || val === null || val === undefined
        ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200">Pending</span>
        : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">Completed</span>,
  },
  { key: "Remarks", label: "Remarks" },
  { key: "Created_At", label: "Date", render: (val: any) => val ? new Date(val).toLocaleDateString() : "—" },
];

function toDateString(d: Date) {
  return d.toISOString().split("T")[0];
}

const TODAY = toDateString(new Date());

export default function TeleRecordPage() {
  const { canView, canCreate, loading: permLoading } = useScreenPermission("telerecords");
  const userDetails = useCurrentUser();
  const { isTelecaller, isBranchIncharge, isZoneManager, branchCode, zoneCode } = useRoleContext();

  const ownUsername = (userDetails as any)?.username || "";
  const showUsernameFilter = !isTelecaller;

  // Filters
  const [selectedUsername, setSelectedUsername] = useState(isTelecaller ? ownUsername : "");
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Telecaller dropdown
  const [telecallers, setTelecallers] = useState<{ id: number; label: string; value: string }[]>([]);
  const [loadingTelecallers, setLoadingTelecallers] = useState(false);

  // Records
  const [records, setRecords] = useState<any[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  // Employee list for add modal (registered users)
  const [employees, setEmployees] = useState<any[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fetchedRef = useRef(false);

  const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<TeleFormData>({
    defaultValues: {
      Purpose: "", Name: "", Phone_No: "", First_Name: "", Last_Name: "",
      Call_Status: "", Coversation_Status: "", Remarks: "",
    },
  });

  const purpose = watch("Purpose");
  const selectedName = watch("Name");
  const isRenewalOrVerification = purpose === "Renewal" || purpose === "Verification";
  const isFollowUp = purpose === "Follow-Up";
  const showConversation = purpose === "Renewal" || purpose === "Follow-Up";

  // Build API params based on role
  const buildParams = useCallback((username: string, date: string, status: string) => {
    const params: Record<string, string> = {};
    if (date) params.Date = date;
    if (status) params.Status = status;
    if (isTelecaller) {
      params.Username = username || ownUsername;
    } else if (username) {
      params.Employee = username;
    }
    return params;
  }, [isTelecaller, ownUsername]);

  // Fetch telecallers for the dropdown
  const fetchTelecallers = useCallback(async () => {
    setLoadingTelecallers(true);
    try {
      const params = isBranchIncharge && branchCode
        ? { Branch_Code: branchCode }
        : isZoneManager && zoneCode
          ? { Zone_Code: zoneCode }
          : undefined;
      const res = await getBranchZoneEmployees('tele', params);
      const data: any[] = Array.isArray(res.data) ? res.data : [];
      setTelecallers(data.map((e, i) => ({
        id: i + 1,
        value: e.user__username,
        label: e.Emp_Name,
      })));
    } catch {
      setTelecallers([]);
    } finally {
      setLoadingTelecallers(false);
    }
  }, [isBranchIncharge, isZoneManager, branchCode, zoneCode]);

  // Fetch employees (for Name dropdown in add modal) — based on selected telecaller username
  const fetchEmployees = useCallback(async (username: string) => {
    if (!username) { setEmployees([]); return; }
    setLoadingEmployees(true);
    try {
      const res = await getBranchZoneEmployees('tele', { Employee: username });
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch {
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  const fetchRecords = useCallback(async (username: string, date: string, status: string) => {
    setLoadingRecords(true);
    try {
      const params = buildParams(username, date, status);
      const res = await getTeleRecords(params);
      const data = res.data as any;
      setRecords(Array.isArray(data) ? data : data?.results ?? []);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }, [buildParams]);

  // Auto-fill phone in modal when name selected
  useEffect(() => {
    if (!isRenewalOrVerification || !selectedName) return;
    const emp = employees.find((e: any) => e.user__username === selectedName);
    if (emp) setValue("Phone_No", emp.Phone || "");
  }, [selectedName, employees, isRenewalOrVerification, setValue]);

  // Reset name/phone when purpose changes
  useEffect(() => {
    setValue("Name", "");
    setValue("Phone_No", "");
    setValue("First_Name", "");
    setValue("Last_Name", "");
  }, [purpose, setValue]);

  useEffect(() => {
    if (!permLoading && canView && showUsernameFilter && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchTelecallers();
    }
  }, [permLoading, canView]);

  const handleUsernameChange = (username: string) => {
    setSelectedUsername(username);
    if (username || isTelecaller) fetchRecords(username, selectedDate, selectedStatus);
    else setRecords([]);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    if (selectedUsername || isTelecaller) fetchRecords(selectedUsername, date, selectedStatus);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    if (selectedUsername || isTelecaller) fetchRecords(selectedUsername, selectedDate, status);
  };

  const handleClearFilters = () => {
    const newUsername = isTelecaller ? ownUsername : "";
    setSelectedUsername(newUsername);
    setSelectedDate(TODAY);
    setSelectedStatus("");
    if (isTelecaller) fetchRecords(ownUsername, TODAY, "");
    else setRecords([]);
  };

  const nameOptions = employees.map((e: any, i: number) => ({
    id: i + 1,
    value: e.user__username,
    label: e.Emp_Name,
  }));

  const onSubmit = async (data: TeleFormData) => {
    if (!data.Purpose) { toast.error("Please select a purpose"); return; }
    if (isRenewalOrVerification && !data.Name) { toast.error("Please select a name"); return; }
    if (isFollowUp && (!data.First_Name || !data.Last_Name)) {
      toast.error("Please fill all name fields"); return;
    }
    if (!data.Phone_No) { toast.error("Please enter phone number"); return; }
    if (!data.Call_Status) { toast.error("Please select call status"); return; }

    const emp = isRenewalOrVerification ? employees.find((e: any) => e.user__username === data.Name) : null;

    const payload: any = {
      Purpose: data.Purpose,
      Phone_No: data.Phone_No,
      Call_Status: data.Call_Status,
      Remarks: data.Remarks,
    };

    if (isRenewalOrVerification && emp) {
      payload.Name = emp.Emp_Name;
      payload.Username = emp.user__username;
    } else if (isFollowUp) {
      payload.Name = `${data.First_Name} ${data.Last_Name}`;
    }

    if (showConversation && data.Coversation_Status !== "") {
      payload.Coversation_Status = Number(data.Coversation_Status);
    }

    setSubmitting(true);
    try {
      const res = await submitTeleRecord(payload);
      if (res.status === 200 || res.status === 201) {
        toast.success("Tele record saved successfully");
        setModalOpen(false);
        reset();
        fetchRecords(selectedUsername, selectedDate, selectedStatus);
      } else {
        toast.error("Failed to save record");
      }
    } catch {
      toast.error("Error saving record");
    } finally {
      setSubmitting(false);
    }
  };

  if (permLoading) return (
    <div className="min-h-screen bg-gray-50">
      {isTelecaller ? <TelecallerHeader /> : <AdminHeader />}
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent" />
      </div>
    </div>
  );

  if (!canView) return (
    <div className="min-h-screen bg-gray-50">
      {isTelecaller ? <TelecallerHeader /> : <AdminHeader />}
      <div className="flex items-center justify-center py-32">
        <p className="text-gray-500">You don't have permission to view tele records.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {isTelecaller ? <TelecallerHeader /> : <AdminHeader />}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-brand-500" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900">Tele Record</h1>
              <p className="text-gray-500 text-sm">Log and track telecaller interactions</p>
            </div>
          </div>
          {canCreate && (
            <button
              onClick={() => { reset(); fetchEmployees(selectedUsername || ownUsername); setModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-700 rounded-lg transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Add Record
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3 sm:gap-4 items-end">

            {/* Username Dropdown — hidden for telecaller */}
            {showUsernameFilter && (
              <div className="w-full sm:flex-1 sm:min-w-[180px]">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telecaller</label>
                <select
                  value={selectedUsername}
                  onChange={e => handleUsernameChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
                  disabled={loadingTelecallers}
                >
                  <option value="">Select telecaller</option>
                  {telecallers.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Date */}
            <div className="w-full sm:flex-1 sm:min-w-[160px]">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={e => handleDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </div>

            {/* Status */}
            <div className="w-full sm:flex-1 sm:min-w-[160px]">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Status</label>
              <select
                value={selectedStatus}
                onChange={e => handleStatusChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
              >
                <option value="">All Statuses</option>
                {CALL_STATUS_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Reset */}
            <div className="flex gap-2">
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Records Table */}
        <GenericTable
          title="Tele Records"
          columns={RECORD_COLUMNS}
          data={records}
          loading={loadingRecords}
          emptyMessage={
            !isTelecaller && !selectedUsername
              ? "Select a telecaller to view records"
              : "No tele records found"
          }
        />
      </main>

      {/* Add Record Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Add New Record</h2>
              <button onClick={() => { setModalOpen(false); reset(); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Dropdown control={control} name="Purpose" label="Purpose" required options={PURPOSE_OPTIONS} placeholder="Select purpose" error={errors.Purpose?.message} />
                <Dropdown control={control} name="Call_Status" label="Call Status" required options={CALL_STATUS_OPTIONS} placeholder="Select call status" error={errors.Call_Status?.message} />
              </div>

              {isRenewalOrVerification && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Dropdown control={control} name="Name" label="Name" required options={nameOptions} placeholder="Select name" isLoading={loadingEmployees} error={errors.Name?.message} />
                  <FormField control={control} name="Phone_No" label="Phone Number" type="text" required placeholder="Auto-filled from selection" error={errors.Phone_No?.message} onClear={() => {}} />
                </div>
              )}

              {isFollowUp && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField control={control} name="First_Name" label="First Name" type="text" required placeholder="Enter first name" error={errors.First_Name?.message} onClear={() => {}} />
                    <FormField control={control} name="Last_Name" label="Last Name" type="text" required placeholder="Enter last name" error={errors.Last_Name?.message} onClear={() => {}} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField control={control} name="Phone_No" label="Phone Number" type="text" required placeholder="Enter phone number" error={errors.Phone_No?.message} onClear={() => {}} />
                    <Dropdown control={control} name="Coversation_Status" label="Conversation Status" options={CONVERSATION_STATUS_OPTIONS} placeholder="Select status" error={errors.Coversation_Status?.message} />
                  </div>
                </>
              )}

              {showConversation && !isFollowUp && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Dropdown control={control} name="Coversation_Status" label="Conversation Status" options={CONVERSATION_STATUS_OPTIONS} placeholder="Select status" error={errors.Coversation_Status?.message} />
                </div>
              )}

              <FormField control={control} name="Remarks" label="Remarks" isTextarea rows={3} placeholder="Enter any remarks or notes…" error={errors.Remarks?.message} onClear={() => {}} />

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setModalOpen(false); reset(); }} className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting || !purpose} className="flex items-center gap-2 px-6 py-2.5 bg-brand-500 hover:bg-brand-700 text-white font-semibold text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <Send className="w-4 h-4" />
                  {submitting ? "Saving…" : "Save Record"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
